import React, { Fragment, useEffect, useState } from "react";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import {
  Box,
  Checkbox,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import CustomTypography from "../../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../../../views/components/common/textFields/TextField";
import DatePicker2 from "../../../../../../../views/components/common/textFields/DatePicker2";
import InlineFormError from "../../../../../../../views/components/common/form/InlineFormError";
import SkuCopyRow from "../../../../../../../views/components/common/SkuCopyRow";
import AppSearchDropdownMulti from "../../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { useDebounce } from "../../../../../../../views/components/common/hooks/Debounce";
import {
  getModernPageColors,
  modernSectionPaperSx,
  modernTableBodyCellSx,
  modernTableHeaderCellSx,
  modernTableRowSx,
} from "../../../../../../../styles/modernListingPageSx";
import SwageazyInventoryAdjustmentStore from "../../../../../stores/SwageazyInventoryAdjustmentStore";
import InventoryAdjustmentDialogShell from "./InventoryAdjustmentDialogShell";
import { StockAdjustmentRow } from "../../../../../../../models/SwageazyInventory/SwageazyInventoryAdjustment";
import {
  AdjustmentReasonField,
  InlineFormSuccess,
  MobileSelectAllBar,
  MobileStockRowCard,
  useIsCompactLayout,
  SkuStockSummaryTable,
  StockEmptyState,
  StockLoadingState,
} from "./InventoryAdjustmentParts";

interface SwageazyInventorySkuTransferPopupProps {
  store: SwageazyInventoryAdjustmentStore;
  onSaved: () => void;
}

const SwageazyInventorySkuTransferPopup: React.FC<
  SwageazyInventorySkuTransferPopupProps
> = ({ store, onSaved }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const headerSx = modernTableHeaderCellSx(theme);
  const cellSx = modernTableBodyCellSx(theme);
  const isCompact = useIsCompactLayout();

  const [productSearch, setProductSearch] = useState("");
  const debouncedProductSearch = useDebounce(productSearch, 400);

  useEffect(() => {
    if (!store.isTransferOpen) return;
    store.searchDestinationProducts(debouncedProductSearch).catch((error) => {
      console.error(error);
    });
  }, [debouncedProductSearch, store, store.isTransferOpen]);

  useEffect(() => {
    if (!store.isTransferOpen) setProductSearch("");
  }, [store.isTransferOpen]);

  const rows = store.transferRows;
  const selectedRows = rows.filter((row) => row.selected);
  const allSelected = rows.length > 0 && selectedRows.length === rows.length;
  const sourceSkus = store.transferSelectedSourceSkus;
  const destinationOptions = store.destinationSkuOptions.map((option) => ({
    id: option.sku,
    value: option.label,
  }));

  const toggleAll = (selected: boolean) =>
    store.setRowsSelected(
      rows.map((row) => row.key),
      selected,
    );

  const renderTransferQtyField = (
    row: StockAdjustmentRow,
    hasError: boolean,
  ) => (
    <CustomTextField
      type="number"
      allowNegativeValue={false}
      allowDecimalValue={false}
      placeholder={`Max ${row.on_hand}`}
      width="100%"
      value={row.quantity}
      error={hasError}
      disabled={store.isSubmitting}
      onChange={(e) => {
        const value = e.target.value;
        store.updateRow(row.key, { quantity: value });
        if (value.trim() && !row.selected) {
          store.setRowsSelected([row.key], true);
        }
      }}
    />
  );

  const handleTransfer = async () => {
    try {
      const saved = await store.submitTransfer();
      if (saved) onSaved();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <InventoryAdjustmentDialogShell
      open={store.isTransferOpen}
      title="Transfer Stock Between SKUs"
      subtitle={
        store.product
          ? `From #${store.product.id} · ${store.product.name}. Units stay in the same box.`
          : ""
      }
      onClose={store.closeTransfer}
      disableClose={store.isSubmitting}
      footer={
        <>
          <AppButton
            variant="transparentPrimary"
            label="Close"
            onClick={store.closeTransfer}
            disabled={store.isSubmitting}
          />
          <AppButton
            variant="primary"
            label={store.isSubmitting ? "Transferring..." : "Transfer"}
            onClick={handleTransfer}
            disabled={store.isSubmitting || store.isStockLoading}
          />
        </>
      }
    >
      {store.isStockLoading && store.rows.length === 0 ? (
        <StockLoadingState />
      ) : store.stockError ? (
        <InlineFormError message={store.stockError} />
      ) : store.rows.length === 0 ? (
        <StockEmptyState message="No box-level stock found for this product." />
      ) : (
        <>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <AppSearchDropdownMulti
                title="Warehouse"
                isMultiSelect={false}
                local
                floatingLabel
                list={store.warehouseOptions}
                value={store.warehouseOptions.filter(
                  (option) => option.id === store.transferWarehouseId,
                )}
                handleSelect={(selected) =>
                  store.setTransferWarehouse(selected?.[0]?.id || "")
                }
                disabled={store.isSubmitting}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 8 }}>
              <AppSearchDropdownMulti
                title="Destination product (name, SKU or ID)"
                isMultiSelect={false}
                floatingLabel
                list={store.destinationSearchResults}
                value={store.destinationProduct ? [store.destinationProduct] : []}
                handleSearch={(text) => setProductSearch(text || "")}
                handleSelect={(selected) => {
                  store
                    .selectDestinationProduct(selected?.[0] || null)
                    .catch((error) => console.error(error));
                }}
                disabled={store.isSubmitting}
              />
            </Grid2>
          </Grid2>

          <SkuStockSummaryTable
            summaries={
              store.transferWarehouseId
                ? store.summaries.filter(
                    (s) => s.warehouse_id === store.transferWarehouseId,
                  )
                : store.summaries
            }
          />

          {!store.transferWarehouseId ? (
            <StockEmptyState message="Select a warehouse to choose boxes." />
          ) : rows.length === 0 ? (
            <StockEmptyState message="No stock in this warehouse." />
          ) : (
            <Box>
              <CustomTypography
                variant="heading7"
                color={colors.title}
                sx={{ mb: 1 }}
              >
                Source Boxes
              </CustomTypography>
              {isCompact ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <MobileSelectAllBar
                    total={rows.length}
                    selectedCount={selectedRows.length}
                    onToggle={toggleAll}
                    disabled={store.isSubmitting}
                  />
                  {rows.map((row) => {
                    const rowError = store.rowErrors[row.key];
                    return (
                      <MobileStockRowCard
                        key={row.key}
                        sku={row.sku}
                        selected={row.selected}
                        onToggle={(selected) =>
                          store.setRowsSelected([row.key], selected)
                        }
                        disabled={store.isSubmitting}
                        error={rowError}
                        meta={[
                          { label: "Box", value: row.box_id },
                          { label: "Rack", value: row.rack || "—" },
                          { label: "In Box", value: row.on_hand },
                        ]}
                      >
                        <Grid2 size={12}>
                          {renderTransferQtyField(row, Boolean(rowError))}
                        </Grid2>
                      </MobileStockRowCard>
                    );
                  })}
                </Box>
              ) : (
                <TableContainer
                  sx={{
                    border: `1px solid ${colors.border}`,
                    borderRadius: 2,
                    maxHeight: "35vh",
                    overflow: "auto",
                  }}
                >
                  <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...headerSx, width: 48 }}>
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={
                              selectedRows.length > 0 && !allSelected
                            }
                            onChange={(e) => toggleAll(e.target.checked)}
                            disabled={store.isSubmitting}
                          />
                        </TableCell>
                        <TableCell sx={headerSx}>SKU</TableCell>
                        <TableCell sx={headerSx}>Box</TableCell>
                        <TableCell sx={headerSx}>Rack</TableCell>
                        <TableCell sx={headerSx}>In Box</TableCell>
                        <TableCell sx={{ ...headerSx, minWidth: 120 }}>
                          Transfer Qty
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => {
                        const rowError = store.rowErrors[row.key];
                        return (
                          <Fragment key={row.key}>
                            <TableRow
                              sx={{
                                ...modernTableRowSx(theme),
                                ...(row.selected && {
                                  bgcolor: colors.surfaceSoft,
                                }),
                              }}
                            >
                              <TableCell sx={cellSx}>
                                <Checkbox
                                  size="small"
                                  checked={row.selected}
                                  onChange={(e) =>
                                    store.setRowsSelected(
                                      [row.key],
                                      e.target.checked,
                                    )
                                  }
                                  disabled={store.isSubmitting}
                                />
                              </TableCell>
                              <TableCell sx={cellSx}>
                                <SkuCopyRow
                                  sku={row.sku}
                                  color={colors.title}
                                  fontWeight={600}
                                  variant="body3"
                                />
                              </TableCell>
                              <TableCell sx={cellSx}>{row.box_id}</TableCell>
                              <TableCell sx={cellSx}>
                                {row.rack || "—"}
                              </TableCell>
                              <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                {row.on_hand}
                              </TableCell>
                              <TableCell sx={cellSx}>
                                {renderTransferQtyField(row, Boolean(rowError))}
                              </TableCell>
                            </TableRow>
                            {rowError && (
                              <TableRow>
                                <TableCell
                                  colSpan={6}
                                  sx={{ py: 0, border: 0 }}
                                >
                                  <InlineFormError message={rowError} />
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {sourceSkus.length > 0 && (
            <Box sx={modernSectionPaperSx(theme)}>
              <CustomTypography
                variant="heading7"
                color={colors.title}
                sx={{ mb: 1.5 }}
              >
                Map SKUs
              </CustomTypography>
              {!store.destinationProduct ? (
                <CustomTypography variant="body2" color={colors.subtitle}>
                  Select a destination product to map SKUs.
                </CustomTypography>
              ) : store.isDestinationLoading ? (
                <CustomTypography variant="body2" color={colors.subtitle}>
                  Loading destination SKUs...
                </CustomTypography>
              ) : destinationOptions.length === 0 ? (
                <InlineFormError message="The destination product has no active SKUs." />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {sourceSkus.map((sourceSku) => {
                    const units = selectedRows
                      .filter((row) => row.sku.toUpperCase() === sourceSku)
                      .reduce(
                        (sum, row) => sum + (Number(row.quantity) || 0),
                        0,
                      );
                    const mapped = store.skuMapping[sourceSku];
                    return (
                      <Grid2
                        key={sourceSku}
                        container
                        spacing={1.5}
                        sx={{ alignItems: "center" }}
                      >
                        <Grid2 size={{ xs: 12, md: 5 }}>
                          <Box>
                            <SkuCopyRow
                              sku={sourceSku}
                              variant="heading7"
                              fontWeight={700}
                              color={colors.title}
                            />
                          </Box>
                          <CustomTypography
                            variant="caption"
                            color={colors.subtitle}
                          >
                            {units} unit(s) selected
                          </CustomTypography>
                        </Grid2>
                        <Grid2
                          size={{ xs: 12, md: 1 }}
                          sx={{ display: "flex", justifyContent: "center" }}
                        >
                          {isCompact ? (
                            <ArrowDownwardIcon
                              sx={{ color: colors.accent, fontSize: 18 }}
                            />
                          ) : (
                            <ArrowForwardIcon
                              sx={{ color: colors.accent, fontSize: 18 }}
                            />
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                          <AppSearchDropdownMulti
                            title="Destination SKU"
                            isMultiSelect={false}
                            local
                            floatingLabel
                            list={destinationOptions}
                            value={destinationOptions.filter(
                              (option) => option.id === mapped,
                            )}
                            handleSelect={(selected) =>
                              store.setSkuMapping(
                                sourceSku,
                                selected?.[0]?.id || "",
                              )
                            }
                            disabled={store.isSubmitting}
                          />
                        </Grid2>
                      </Grid2>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <DatePicker2
                type="date"
                label="Expiry date (if destination tracks expiry)"
                value={
                  store.transferExpiryDate
                    ? dayjs(store.transferExpiryDate)
                    : null
                }
                minDate={dayjs()}
                onChange={(value) =>
                  store.setTransferExpiryDate(
                    value ? value.format("YYYY-MM-DD") : "",
                  )
                }
                borderRadius="8px"
                zIndex={4000}
                disabled={store.isSubmitting}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 8 }}>
              <AdjustmentReasonField
                value={store.reason}
                onChange={store.setReason}
                disabled={store.isSubmitting}
                label="Comments"
              />
            </Grid2>
          </Grid2>
        </>
      )}

      <InlineFormError message={store.formError} sx={{ mt: 0, mb: 0 }} />
      <InlineFormSuccess message={store.successMessage} />
    </InventoryAdjustmentDialogShell>
  );
};

export default observer(SwageazyInventorySkuTransferPopup);
